import { useState, useCallback, useRef } from 'react';
import { ProjectData } from '../types';

interface UseEventDrivenAutoSaveProps {
  jiraKey: string;
  updateFormData: (formData: Partial<ProjectData['formData']>) => Promise<void>;
  saveToLocalStorage: (formData: Partial<ProjectData['formData']>, currentStep: number, completedSteps: number[]) => void;
  currentStep: number;
  completedSteps: number[];
  isOnline: boolean;
}

interface UseEventDrivenAutoSaveReturn {
  saveField: (fieldName: string, value: any) => void;
  saveFieldDebounced: (fieldName: string, value: any) => void;
  saveAllFields: (formData: Partial<ProjectData['formData']>) => void;
  lastSaved: Date | null;
  isSaving: boolean;
  saveError: string | null;
}

export function useEventDrivenAutoSave({
  jiraKey,
  updateFormData,
  saveToLocalStorage,
  currentStep,
  completedSteps,
  isOnline,
}: UseEventDrivenAutoSaveProps): UseEventDrivenAutoSaveReturn {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // Track pending changes to avoid duplicate saves
  const pendingChanges = useRef<Map<string, any>>(new Map());
  const debounceTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const lastSavedValues = useRef<Map<string, any>>(new Map());
  
  // Save a single field immediately
  const saveField = useCallback(async (fieldName: string, value: any) => {
    if (!fieldName || !jiraKey) return;
    
    // Check if value has actually changed to prevent unnecessary saves
    const lastSavedValue = lastSavedValues.current.get(fieldName);
    if (lastSavedValue === value) {
      return; // No change, skip save
    }
    
    // Clear any pending debounced save for this field
    const existingTimeout = debounceTimeouts.current.get(fieldName);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      debounceTimeouts.current.delete(fieldName);
    }
    
    // Clear from pending changes
    pendingChanges.current.delete(fieldName);
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const fieldData = { [fieldName]: value };
      
      // Save to local storage immediately
      saveToLocalStorage(fieldData, currentStep, completedSteps);
      
      // Save to database if online
      if (isOnline) {
        await updateFormData(fieldData);
        setLastSaved(new Date());
      }
      
      // Update last saved value
      lastSavedValues.current.set(fieldName, value);
    } catch (error) {
      console.error('❌ Error saving field:', fieldName, error);
      setSaveError(error instanceof Error ? error.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [jiraKey, updateFormData, saveToLocalStorage, currentStep, completedSteps, isOnline]);
  
  // Save a field with debounce (for all fields)
  const saveFieldDebounced = useCallback((fieldName: string, value: any) => {
    if (!fieldName || !jiraKey) return;
    
    // Clear existing timeout for this field
    const existingTimeout = debounceTimeouts.current.get(fieldName);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Store pending change
    pendingChanges.current.set(fieldName, value);
    
    // Set new timeout (1 second for better performance)
    const timeout = setTimeout(() => {
      const finalValue = pendingChanges.current.get(fieldName);
      if (finalValue !== undefined) {
        pendingChanges.current.delete(fieldName);
        debounceTimeouts.current.delete(fieldName);
        saveField(fieldName, finalValue);
      }
    }, 1000); // 1 second debounce to reduce spam
    
    debounceTimeouts.current.set(fieldName, timeout);
  }, [saveField, jiraKey]);
  
  // Save all fields at once
  const saveAllFields = useCallback(async (formData: Partial<ProjectData['formData']>) => {
    if (!jiraKey) return;
    
    // Clear all pending debounced saves
    debounceTimeouts.current.forEach(timeout => clearTimeout(timeout));
    debounceTimeouts.current.clear();
    pendingChanges.current.clear();
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // Save to local storage immediately
      saveToLocalStorage(formData, currentStep, completedSteps);
      
      // Save to database if online
      if (isOnline) {
        await updateFormData(formData);
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('❌ Error saving all fields:', error);
      setSaveError(error instanceof Error ? error.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [jiraKey, updateFormData, saveToLocalStorage, currentStep, completedSteps, isOnline]);
  
  return {
    saveField,
    saveFieldDebounced,
    saveAllFields,
    lastSaved,
    isSaving,
    saveError,
  };
}
